import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../../store';

const API_BASE = '/api';

function getHeaders() {
  const token = useAuthStore.getState().token;
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export function VictoriaMigrationPanel() {
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePreview = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/victoria-migration/preview`, { headers: getHeaders() });
      const data = await res.json();
      setPreview(data);
    } catch (err) {
      toast.error('Preview failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!window.confirm('Apply Victoria entity migration? This is idempotent.')) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/victoria-migration/apply`, { method: 'POST', headers: getHeaders() });
      const data = await res.json();
      setApplyResult(data);
      toast.success(`Migration ${data.status}`);
    } catch (err) {
      toast.error('Apply failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Victoria Entity Migration</CardTitle>
        <CardDescription>Separate Victoria documents into own entity scope</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handlePreview} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Preview
          </Button>
          <Button size="sm" onClick={handleApply} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Apply
          </Button>
        </div>

        {preview && preview.status === 'already_applied' && (
          <div className="text-sm">
            <Badge variant="outline" className="text-green-600">Already applied</Badge>
            <span className="ml-2 text-muted-foreground">at {preview.applied_at}</span>
          </div>
        )}

        {preview && preview.collections && (
          <div className="text-sm space-y-2">
            {Object.entries(preview.collections).map(([coll, info]) => (
              <div key={coll}>
                <span className="font-medium">{coll}</span>: {info.count} docs
                {info.samples?.length > 0 && (
                  <pre className="mt-1 text-xs bg-muted p-2 rounded overflow-auto max-h-32">
                    {JSON.stringify(info.samples.slice(0, 3), null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}

        {applyResult && (
          <div className="text-sm">
            <Badge variant={applyResult.status === 'applied' ? 'default' : 'outline'}>
              {applyResult.status}
            </Badge>
            {applyResult.counts && (
              <span className="ml-2 text-muted-foreground">
                {Object.entries(applyResult.counts).map(([k, v]) => `${k}: ${v}`).join(', ')}
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
